import type { PollAssignment, VmPollOverlay } from 'Components/organisms/vm-poll-overlay/vm-poll-overlay';

/**
 * Polls Controller
 *
 * Thin coordination layer between the Brightcove player and the vm-poll-overlay
 * component. Listens to player timeline events and triggers polls.
 *
 * A poll only shows when the user naturally watches through its trigger point.
 * Seeking past or into a poll's window does not trigger it.
 *
 * Seek detection uses time-jump comparison rather than seeking/seeked events,
 * because browsers fire timeupdate with the new position before seeked fires.
 * See: https://github.com/videojs/video.js/issues/3446
 */

// timeupdate fires ~4x/sec during playback. Normal tick delta is ~0.25s.
// A jump larger than this threshold indicates a seek.
const SEEK_THRESHOLD_SECONDS = 2;

export class PollsController {
    private _overlay: VmPollOverlay;
    private _assignments: PollAssignment[];
    private _player: any;
    private _lastTime: number = -1;
    private _pausedByPoll: boolean = false;

    constructor(overlay: VmPollOverlay, player: any, assignments: PollAssignment[]) {
        this._overlay = overlay;
        this._assignments = assignments;
        this._player = player;

        overlay.assignments = assignments;
        player.on('timeupdate', () => this._onTimeUpdate());
        overlay.addEventListener('poll-hover', () => this._onPollHover());
        overlay.addEventListener('poll-hidden', () => this._onPollHidden());
    }

    private _onPollHover() {
        if (this._pausedByPoll) return;
        if (this._player.paused() || this._player.ended()) return;
        this._player.pause();
        this._pausedByPoll = true;
    }

    private _onPollHidden() {
        if (!this._pausedByPoll) return;
        this._pausedByPoll = false;
        this._player.play();
    }

    private _onTimeUpdate() {
        const currentTime = this._player.currentTime();

        // First tick or seek detected — update position without triggering
        if (this._lastTime < 0 || Math.abs(currentTime - this._lastTime) > SEEK_THRESHOLD_SECONDS) {
            this._lastTime = currentTime;
            return;
        }

        for (const assignment of this._assignments) {
            if (this._overlay.hasBeenShown(assignment.id)) continue;

            const trigger = assignment.trigger_seconds;

            if (this._lastTime < trigger && currentTime >= trigger) {
                this._overlay.showPoll(assignment.id);
                break;
            }
        }

        this._lastTime = currentTime;
    }
}
